import { FC } from 'react';
import { Grid } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title, RadioLabel } from 'src/generic/component';
import { BooleanRadio, Input } from 'src/generic/component/form';
import { convertToFormattedDate, convertToIsoDate } from 'src/utils';
import { Draft } from 'src/types/common';
import { IAIRequest } from 'src/types/org/ai/schoolInterface';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { AuthorizationLevel } from './AuthorizationLevel';

interface IOrganizationInfoPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const OrganizationInformationPanel: FC<IOrganizationInfoPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { getError, hasError } = ErrorManager(errors);

  // Access fields from the new structure
  const org = item.data?.org;
  const appForm = item.data?.appForm;
  const ai = item.data?.ai;

  const {
    name: name = '',
    phone = '',
    website = '',
    email = ''
  } = org || {};

  const {
    establishedDate,
    isSchoolDistrictMember,
    schoolDistricts,
    sharedOrgAddress,
    sharedOrgAddressInfo,
    existingOrg,
    existingOrgInfo,
    hasCollegeBoardCode,
    collegeBoardCodes,
    mergerSchoolCodes
  } = appForm || {};

  const { appliedLevel } = ai || {};

  const changed = (updates: Partial<{
    org?: Partial<typeof org>;
    appForm?: Partial<typeof appForm>;
    ai?: Partial<typeof ai>;
  }>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        org: { ...org, ...updates.org },
        appForm: { ...appForm, ...updates.appForm },
        ai: { ...ai, ...updates.ai }
      }
    };
    onUpdate(updatedItem);
  };

  return (
    <Panel>
      <Title>Organization Contact Information</Title>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel variant="body1">Organization Name</FieldLabel>
            <div style={{
              marginBottom: '8px',
              color: 'rgba(0, 0, 0, 0.6)',
              fontSize: '0.99rem',
              lineHeight: '1.43',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
            }}>
              Please list the official school name including the individual campus distinction (if your organization has multiple campuses)
            </div>
            <Input
              disabled={readOnly}
              value={name}
              validation={hasError('data.org.name') ? 'error' : undefined}
              validationMsg={getError('data.org.name')}
              onChange={(e) => { changed({ org: { name: e.target.value } }); }}
            />
          </FieldRow>

          <AuthorizationLevel
            item={{ appliedLevel }}
            onUpdate={(data) => changed({ ai: { appliedLevel: data.appliedLevel } })}
            errors={errors}
            readOnly={readOnly}
          />

          <FieldRow>
            <FieldLabel variant="body1">Organization General Phone Number</FieldLabel>
            <Input
              disabled={readOnly}
              validation={hasError('data.org.phone') ? 'error' : undefined}
              validationMsg={getError('data.org.phone')}
              onChange={(e) => { changed({ org: { phone: e.target.value || undefined } }); }}
              value={phone}
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">Organization General Website (home page)</FieldLabel>
            <Input
              disabled={readOnly}
              validation={hasError('data.org.website') ? 'error' : undefined}
              validationMsg={getError('data.org.website')}
              onChange={(e) => { changed({ org: { website: e.target.value || undefined } }); }}
              value={website}
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel variant="body1">Organization General Email</FieldLabel>
            <Input
              disabled={readOnly}
              validation={hasError('data.org.email') ? 'error' : undefined}
              validationMsg={getError('data.org.email')}
              onChange={(e) => { changed({ org: { email: e.target.value || undefined } }); }}
              value={email}
            />
          </FieldRow>
        </Grid>

        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">Is your organization a member of a school district or school network?</RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.isSchoolDistrictMember')}
              value={isSchoolDistrictMember}
              name='isSchoolDistrictMember'
              onChange={(value) => {
                changed({
                  appForm: {
                    isSchoolDistrictMember: value,
                    schoolDistricts: value ? schoolDistricts : undefined
                  }
                });
              }}
            />
          </FieldRow>
          {isSchoolDistrictMember &&
            <FieldRow>
              <FieldLabel variant="body1">Please list the school district or school network</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasError('data.appForm.schoolDistricts') ? 'error' : undefined}
                validationMsg={getError('data.appForm.schoolDistricts')}
                onChange={(e) => { changed({ appForm: { schoolDistricts: e.target.value } }); }}
                value={schoolDistricts || ''}
              />
            </FieldRow>
          }

          <FieldRow>
            {readOnly ?
              <FieldLabel>Established Date: {convertToFormattedDate(establishedDate)}</FieldLabel> :
              <>
                <FieldLabel variant="body1">Please select the date your organization was established</FieldLabel>
                <Input type="date" value={convertToIsoDate(establishedDate)}
                  validation={getError('data.appForm.establishedDate') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.establishedDate')}
                  onChange={(e) => { changed({ appForm: { establishedDate: e.target.value } }); }} />
              </>
            }
          </FieldRow>

          <FieldRow>
            <RadioLabel variant="body1">Does your organization share the address or building with another organization?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('data.appForm.sharedOrgAddress')} value={sharedOrgAddress} name='sharedOrgAddress'
              onChange={(value) => {
                changed({
                  appForm: {
                    sharedOrgAddress: value,
                    sharedOrgAddressInfo: value ? sharedOrgAddressInfo : undefined
                  }
                });
              }} />
          </FieldRow>
          {sharedOrgAddress &&
            <FieldRow>
              <FieldLabel variant="body1">Please list the organization(s)</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasError('data.appForm.sharedOrgAddressInfo') ? 'error' : undefined}
                validationMsg={getError('data.appForm.sharedOrgAddressInfo')}
                onChange={(e) => { changed({ appForm: { sharedOrgAddressInfo: e.target.value } }); }}
                value={sharedOrgAddressInfo || ''}
              />
            </FieldRow>
          }

          <FieldRow>
            <RadioLabel variant="body1">Has your organization ever used another name or operated at another address?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('data.appForm.existingOrg')} value={existingOrg} name='existingOrg' onChange={(value) => {
              changed({
                appForm: {
                  existingOrg: value,
                  existingOrgInfo: value ? existingOrgInfo : undefined
                }
              });
            }} />
          </FieldRow>
          {existingOrg &&
            <FieldRow>
              <FieldLabel variant="body1">Please provide the previously used name and/or address</FieldLabel>
              <Input disabled={readOnly} validation={hasError('data.appForm.existingOrgInfo') ? 'error' : undefined} validationMsg={getError('data.appForm.existingOrgInfo')}
                onChange={(e) => {
                  changed({ appForm: { existingOrgInfo: e.target.value } });
                }}
                value={existingOrgInfo || ''}
              />
            </FieldRow>
          }

          <FieldRow>
            <RadioLabel variant="body1">Has your organization ever been assigned or used a College Board school code?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('data.appForm.hasCollegeBoardCode')} value={hasCollegeBoardCode} name='hasCollegeBoardCode'
              onChange={(value) => {
                changed({
                  appForm: {
                    hasCollegeBoardCode: value,
                    collegeBoardCodes: value ? collegeBoardCodes : undefined,
                    mergerSchoolCodes: value ? mergerSchoolCodes : undefined
                  }
                });
              }} />
          </FieldRow>
          {hasCollegeBoardCode &&
            <>
              <FieldRow>
                <FieldLabel variant="body1">Please list the College Board School Code(s)</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('data.appForm.collegeBoardCodes') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.collegeBoardCodes')}
                  onChange={(e) => { changed({ appForm: { collegeBoardCodes: e.target.value } }); }}
                  value={collegeBoardCodes || ''}
                />
              </FieldRow>
              <FieldRow>
                <FieldLabel variant="body1">If a merger, list all schools/codes affected</FieldLabel>
                <Input
                  disabled={readOnly}
                  validation={hasError('data.appForm.mergerSchoolCodes') ? 'error' : undefined}
                  validationMsg={getError('data.appForm.mergerSchoolCodes')}
                  onChange={(e) => { changed({ appForm: { mergerSchoolCodes: e.target.value } }); }}
                  value={mergerSchoolCodes || ''}
                />
              </FieldRow>
            </>
          }
        </Grid>
      </Grid>
    </Panel>
  );
};